import { UAParser } from 'ua-parser-js';

/**
 * Schema-aligned device-type bucket. Mirrors the
 * `device_info.device_type` enum in the generated analytics schema.
 */
export type DeviceType = 'desktop' | 'phone' | 'tablet' | 'console' | 'smarttv';

/**
 * Parsed user-agent values shaped to match the analytics
 * `device_info` / `marketing_name` fields. Cached at module
 * scope so repeated calls return the same parse result.
 */
export interface UserAgentInfo {
	browserName: string;
	browserVersion: string;
	deviceType: DeviceType;
	marketingName: string;
	osName: string;
	osVersion: string;
}

const UA_PARSER_DEVICE_TYPES = {
	CONSOLE: 'console',
	MOBILE: 'mobile',
	SMARTTV: 'smarttv',
	TABLET: 'tablet',
} as const;

let cached: Promise<UserAgentInfo> | null = null;

/**
 * Returns the parsed user-agent info for the current session,
 * memoized for the lifetime of the document. Safe to call from
 * any number of callers without re-running `UAParser`.
 * @returns {Promise<UserAgentInfo>} Parsed UA fields shaped for analytics emission.
 */
export function getUserAgentInfo(): Promise<UserAgentInfo> {
	if (cached === null) {
		cached = parseUserAgent();
	}
	return cached;
}

/**
 * Test-only escape hatch. Clears the memoized parse result so
 * fixtures can re-run with a different mocked `navigator`.
 * @returns {void}
 */
export function __resetUserAgentInfoForTests(): void {
	cached = null;
}

/**
 * Runs UA-Parser once and shapes the result for the analytics schema.
 * @returns {Promise<UserAgentInfo>} Fresh parsed UA info (no memoization).
 */
async function parseUserAgent(): Promise<UserAgentInfo> {
	const { browser, device, os } = await UAParser().withFeatureCheck();
	const browserName = browser.name ?? '';
	const browserVersion = browser.version ?? '';

	return {
		browserName,
		browserVersion,
		deviceType: mapDeviceType(device.type),
		marketingName: buildMarketingName(browserName, browserVersion),
		osName: os.name ?? '',
		osVersion: os.version ?? '',
	};
}

/**
 * Composes the schema's `marketing_name` value ("Chrome 120.0").
 * @param {string} name UA-parsed browser name.
 * @param {string} version UA-parsed browser version.
 * @returns {string} Concatenated `"<name> <version>"`, or just the name when version is missing.
 */
function buildMarketingName(name: string, version: string): string {
	if (!name) return '';
	return version ? `${name} ${version}` : name;
}

/**
 * Maps UA-Parser's free-form `device.type` string to the schema's
 * enumerated `device_type` values.
 * @param {string | undefined} type Raw device type from UA-Parser (may be undefined for desktops).
 * @returns {DeviceType} Schema-aligned device type bucket.
 */
function mapDeviceType(type: string | undefined): DeviceType {
	switch (type) {
		case UA_PARSER_DEVICE_TYPES.MOBILE:
			return 'phone';
		case UA_PARSER_DEVICE_TYPES.TABLET:
			return 'tablet';
		case UA_PARSER_DEVICE_TYPES.CONSOLE:
			return 'console';
		case UA_PARSER_DEVICE_TYPES.SMARTTV:
			return 'smarttv';
		default:
			return 'desktop';
	}
}
